import React from 'react';

import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import {
  UnpublishedData,
  SpendTypeEnum,
  ITransactionItem,
  ITaxonomyItem,
  IRuleItem,
  ISpendDataInfo,
  PublishedModal,
} from '..';
import {
  getTaxonomy,
  saveTransactions,
  saveRules,
  getTransactionsInfo,
  publishSpendData,
} from 'utils/api';
import { LoaderWrapper } from './UnpublishedData.styles';
import { ApiContext } from 'context/ApiContext';

const SUMMARY_URL = '/spend';

interface IProps {
  spendType: SpendTypeEnum;
}

interface IState {
  taxonomyOptions: null | ITaxonomyItem[];
  spendDataInfo: null | ISpendDataInfo;
  moreSpendDataInfo: null | ISpendDataInfo;
  isPublishing: boolean;
  isPublishedModalOpen: boolean;
}

export class UnpublishedDataContainer extends React.Component<IProps, IState> {
  state = {
    taxonomyOptions: null as IState['taxonomyOptions'],
    spendDataInfo: null as IState['spendDataInfo'],
    moreSpendDataInfo: null as IState['moreSpendDataInfo'],
    isPublishing: false,
    isPublishedModalOpen: false,
  };

  componentDidMount() {
    this.fetchTaxonomyOptions();
    this.fetchSpendDataInfo();
  }

  fetchTaxonomyOptions = () => {
    const { spendType } = this.props;

    getTaxonomy(spendType)
      .then((taxonomyOptions) => {
        this.setState({ taxonomyOptions });
      })
      .catch(() => {
        this.setState({ taxonomyOptions: [] });
      });
  };

  fetchSpendDataInfo = () => {
    const { spendType } = this.props;

    return getTransactionsInfo(spendType).then((spendDataInfo) => {
      this.setState({ spendDataInfo });
    });
  };

  updateTransactions = (data: ITransactionItem[]) => {
    const { spendType } = this.props;

    return saveTransactions(spendType, data);
  };

  updateRules = (data: IRuleItem[]) => {
    const { spendType } = this.props;

    return saveRules(spendType, data);
  };

  getMoreClassification = () => {
    const { spendType } = this.props;
    const moreType =
      spendType === SpendTypeEnum.THIRD_PARTY
        ? SpendTypeEnum.PERSONNEL
        : SpendTypeEnum.THIRD_PARTY;

    return getTransactionsInfo(moreType).then((moreSpendDataInfo) => {
      this.setState({ moreSpendDataInfo });
    });
  };

  publish = () => {
    const { spendType } = this.props;

    this.closePublishedModal();
    this.setState({ isPublishing: true });

    return Promise.all([
      publishSpendData(spendType),
      this.getMoreClassification(),
    ]).then(() => {
      this.setState({ isPublishing: false, isPublishedModalOpen: true });
    });
  };

  closePublishedModal = () => {
    this.setState({ isPublishedModalOpen: false });
  };

  navigateToClassification = (spendType: SpendTypeEnum) => {
    this.closePublishedModal();
    window.location.href = `/classification/${spendType}`;
  };

  navigateToSummary = () => {
    this.closePublishedModal();
    window.location.href = SUMMARY_URL;
  };

  render() {
    const {
      taxonomyOptions,
      spendDataInfo,
      moreSpendDataInfo,
      isPublishing,
      isPublishedModalOpen,
    } = this.state;
    const { spendType } = this.props;

    if (!taxonomyOptions || isPublishing) {
      return (
        <LoaderWrapper>
          <ApLoaderDefault loaderText={() => <ApLoaderText />} />
        </LoaderWrapper>
      );
    }

    return (
      <ApiContext.Consumer>
        {({ permissions }) => (
          <React.Fragment>
            <UnpublishedData
              spendType={spendType}
              taxonomyOptions={taxonomyOptions}
              updateTransactions={this.updateTransactions}
              updateRules={this.updateRules}
              fetchSpendDataInfo={this.fetchSpendDataInfo}
              spendDataInfo={spendDataInfo}
              publish={this.publish}
              permissions={permissions}
            />
            {isPublishedModalOpen && (
              <PublishedModal
                isOpen={isPublishedModalOpen}
                spendType={spendType}
                onClose={this.closePublishedModal}
                onNavigate={this.navigateToSummary}
                onClassifyMore={this.navigateToClassification}
                hasMoreClassification={
                  !!moreSpendDataInfo && moreSpendDataInfo.count > 0
                }
              />
            )}
          </React.Fragment>
        )}
      </ApiContext.Consumer>
    );
  }
}

export default UnpublishedDataContainer;
